import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { X, Upload, AlertCircle, Home, BedDouble, Bath, Ruler, DollarSign, FileText } from 'lucide-react';

const UploadModal = ({
  isOpen, isDragging, uploadProgress, uploadError, isPublic,
  onVisibilityChange, onDragOver, onDragEnter, onDragLeave, onDrop, onFileSelect, onClose,
}) => {
  const [metadata, setMetadata] = useState({
    bedrooms: '', bathrooms: '', sqft: '', price: '', type: 'upload', description: ''
  });

  if (!isOpen) return null;
  const isUploading = uploadProgress > 0 && uploadProgress < 100;

  const handleMetaChange = (field, value) => setMetadata(prev => ({ ...prev, [field]: value }));

  // Intercept file select to inject metadata into formData
  const handleFileSelect = (e) => {
    if (!e.target.files?.[0]) return;
    // Attach metadata to the event so App.js can read it
    e._metadata = metadata;
    onFileSelect(e);
  };

  const handleDrop = (e) => {
    e._metadata = metadata;
    onDrop(e);
  };

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.2)',
    color: 'white', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '0.375rem' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'linear-gradient(145deg,#0f172a,#1e293b)', borderRadius: '1rem', padding: '1.5rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid rgba(148,163,184,0.1)', boxShadow: '0 25px 50px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ color: 'white', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Upload 3D Model</h2>
            <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '2px 0 0' }}>Add property details for AI analysis</p>
          </div>
          <button onClick={onClose} disabled={isUploading} style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.5rem', padding: '0.5rem', color: '#f87171', cursor: 'pointer', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {uploadError && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <AlertCircle size={16} color="#fca5a5" style={{ flexShrink: 0, marginTop: '1px' }} />
            <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0 }}>{uploadError}</p>
          </div>
        )}

        {isUploading && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '6px' }}>Uploading: {uploadProgress}%</p>
            <div style={{ width: '100%', height: '4px', background: 'rgba(148,163,184,0.15)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'linear-gradient(90deg,#3b82f6,#60a5fa)', borderRadius: '2px', transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {/* Property Details */}
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Property Details</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}><BedDouble size={12} /> Bedrooms</label>
              <input type="number" min="0" placeholder="e.g. 3" value={metadata.bedrooms} onChange={e => handleMetaChange('bedrooms', e.target.value)} style={inputStyle} disabled={isUploading} />
            </div>
            <div>
              <label style={labelStyle}><Bath size={12} /> Bathrooms</label>
              <input type="number" min="0" step="0.5" placeholder="e.g. 2" value={metadata.bathrooms} onChange={e => handleMetaChange('bathrooms', e.target.value)} style={inputStyle} disabled={isUploading} />
            </div>
            <div>
              <label style={labelStyle}><Ruler size={12} /> Square Footage</label>
              <input type="number" min="0" placeholder="e.g. 1800" value={metadata.sqft} onChange={e => handleMetaChange('sqft', e.target.value)} style={inputStyle} disabled={isUploading} />
            </div>
            <div>
              <label style={labelStyle}><DollarSign size={12} /> Price ($)</label>
              <input type="number" min="0" placeholder="e.g. 450000" value={metadata.price} onChange={e => handleMetaChange('price', e.target.value)} style={inputStyle} disabled={isUploading} />
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}><Home size={12} /> Property Type</label>
            <select value={metadata.type} onChange={e => handleMetaChange('type', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }} disabled={isUploading}>
              <option value="upload">Residential</option>
              <option value="condo">Condo</option>
              <option value="townhouse">Townhouse</option>
              <option value="commercial">Commercial</option>
              <option value="land">Land</option>
              <option value="kiri-scan">3D Scan</option>
            </select>
          </div>
        </div>

        {/* Visibility */}
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visibility</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[{ label: '🔒 Private', val: false }, { label: '🌐 Public', val: true }].map(({ label, val }) => (
              <button key={label} type="button" disabled={isUploading} onClick={() => onVisibilityChange(val)}
                style={{ flex: 1, padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid rgba(148,163,184,0.2)', background: isPublic === val ? (val ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)') : 'rgba(30,41,59,0.4)', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem', transition: 'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Drop Zone */}
        <div onDragOver={onDragOver} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDrop={handleDrop}
          style={{ border: `2px dashed ${isDragging ? '#3b82f6' : 'rgba(148,163,184,0.25)'}`, borderRadius: '0.75rem', padding: '2rem', textAlign: 'center', background: isDragging ? 'rgba(59,130,246,0.08)' : 'rgba(15,23,42,0.4)', cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.5 : 1, transition: 'all 0.2s' }}
          onClick={() => !isUploading && document.getElementById('file-upload-input').click()}>
          <input type="file" accept=".glb,.gltf,.ply" onChange={handleFileSelect} disabled={isUploading} style={{ display: 'none' }} id="file-upload-input" />
          <Upload size={40} color={isDragging ? '#3b82f6' : '#334155'} style={{ margin: '0 auto 0.75rem' }} />
          <p style={{ color: isDragging ? '#60a5fa' : '#94a3b8', fontWeight: 600, margin: '0 0 4px', fontSize: '0.9rem' }}>
            {isDragging ? 'Drop your file here' : 'Drag & Drop or Click to Upload'}
          </p>
          <p style={{ color: '#475569', fontSize: '0.75rem', margin: 0 }}>GLB, GLTF, or PLY — Max 100MB</p>
        </div>

        <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: '0.5rem' }}>
          <p style={{ color: '#a89040', fontSize: '0.75rem', margin: 0 }}>
            💡 <strong>AI Tip:</strong> Filling in property details lets the AI answer questions about bedrooms, price, and square footage accurately.
          </p>
        </div>
      </div>
    </div>
  );
};

UploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  uploadProgress: PropTypes.number.isRequired,
  uploadError: PropTypes.string,
  isPublic: PropTypes.bool,
  onVisibilityChange: PropTypes.func,
  onDragOver: PropTypes.func.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

UploadModal.defaultProps = { uploadError: null, isPublic: false, onVisibilityChange: () => {} };

export default UploadModal;
